import React, { useEffect, useState } from 'react';
import { Printer, FileText, Loader2, AlertTriangle, Clock, Scale, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { dossierAPI } from '@/utils/api';

/**
 * Pack « contestation devant le juge de l'exécution » — livrable payant.
 *
 * Contient la lettre de dénonciation au commissaire de justice qui a pratiqué la
 * saisie (formalité obligatoire, à peine d'irrecevabilité) et l'échéancier de la
 * contestation.
 *
 * Point capital d'interface : ce courrier NE SAISIT PAS le juge. La saisine se fait
 * par assignation délivrée par un commissaire de justice. L'écran doit le dire
 * clairement — un utilisateur qui croit avoir agi en postant cette lettre laisse
 * courir son délai et perd son recours.
 *
 * Impression : on écrit le courrier seul dans une iframe isolée puis on l'imprime,
 * comme DemandePiecesLetter. (L'approche `visibility:hidden` laissait la hauteur des
 * autres éléments dans le flux → des dizaines de pages blanches.)
 *
 * Props :
 *   - dossierId : id du dossier
 *   - force     : régénère en ignorant le cache serveur (usage admin / itération prompt)
 */
const esc = (s) =>
  String(s == null ? '' : s).replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])
  );

const formatDateFr = (iso) => {
  if (!iso) return null;
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
};

/**
 * Le pack n'a de sens que si une mesure d'exécution est effectivement en cours.
 * Proposer une contestation devant le juge de l'exécution sur une simple relance de
 * recouvrement décrédibilise l'analyse. On teste donc l'analyse déjà chargée côté
 * client, sans appeler le serveur — donc sans consommer de tokens pour rien.
 */
export const hasMesureExecution = (analysis) => {
  if (!analysis) return false;
  const classification = (analysis.details_structures || {}).classification || {};
  const actions = (analysis.LV3_analyse_juridique_payante || {}).recommandations_actions || [];
  const haystack = [
    classification.type_procedure,
    classification.statut_procedure,
    ...(Array.isArray(actions) ? actions.map((a) => `${a?.action || ''} ${a?.justification || ''}`) : []),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
    .replace(/\u2019/g, "'");
  return /saisie|juge de l'ex|commandement|expulsion/.test(haystack);
};

const SaisineJexPack = ({ dossierId, force = false }) => {
  const [pack, setPack] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const [expediteur, setExpediteur] = useState('');
  const [destAdresse, setDestAdresse] = useState('');
  const [lieuDate, setLieuDate] = useState('');

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await dossierAPI.getDenonciationHuissier(dossierId, force);
        if (!active) return;
        setPack(data);
        setExpediteur(data.expediteur || '');
        setDestAdresse((data.destinataire && data.destinataire.adresse) || '');
        setLieuDate(data.lieu_date || '');
      } catch (e) {
        if (active) setError(true);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [dossierId, force]);

  if (loading) {
    return (
      <div className="card p-8 mb-8 flex items-center justify-center gap-3 text-gray-600">
        <Loader2 className="animate-spin" size={20} />
        Préparation de votre dossier de contestation...
      </div>
    );
  }

  if (error || !pack) {
    return (
      <div className="card p-8 mb-8 text-center text-gray-600">
        Votre dossier de contestation n'a pas pu être généré pour le moment. Réessayez dans quelques instants.
      </div>
    );
  }

  const ech = pack.echeances || {};
  const etapes = Array.isArray(ech.etapes) ? ech.etapes : [];
  const corps = Array.isArray(pack.corps_paragraphes) ? pack.corps_paragraphes : [];
  const destNom = (pack.destinataire && pack.destinataire.nom) || '[Nom du commissaire de justice]';

  // Urgence : pilote la couleur du bandeau. Le délai expiré reste affiché — c'est une
  // information que l'utilisateur doit avoir, pas une raison de masquer l'écran.
  const jours = typeof ech.jours_restants === 'number' ? ech.jours_restants : null;
  const urgence =
    jours === null ? 'neutre' : jours < 0 ? 'expire' : jours <= 7 ? 'critique' : jours <= 15 ? 'tendu' : 'normal';
  const couleurs = {
    expire: { bg: '#fef2f2', border: '#fecaca', text: '#991b1b' },
    critique: { bg: '#fef2f2', border: '#fecaca', text: '#b91c1c' },
    tendu: { bg: '#fff7ed', border: '#fed7aa', text: '#c2410c' },
    normal: { bg: '#eff6ff', border: '#bfdbfe', text: '#1e40af' },
    neutre: { bg: '#fffbeb', border: '#fde68a', text: '#92400e' },
  }[urgence];

  const buildLetterHtml = () => {
    const nl2br = (s) => esc(s).replace(/\n/g, '<br>');
    const paras = corps.map((p) => `<p style="margin:0 0 12px;text-align:justify">${esc(p)}</p>`).join('');
    const formule =
      pack.formule_politesse ||
      "Je vous prie d'agréer, Madame, Monsieur, l'expression de mes salutations distinguées.";
    return `<!doctype html><html lang="fr"><head><meta charset="utf-8">
<title>Dénonciation de contestation</title>
<style>
  @page { size: A4; margin: 2cm; }
  body { font-family: Georgia, 'Times New Roman', serif; color:#111; font-size:12pt; line-height:1.5; margin:0; }
  .exp { white-space:pre-line; margin-bottom:32px; }
  .dest { text-align:right; white-space:pre-line; margin-bottom:32px; }
  .date { text-align:right; margin-bottom:32px; }
  .lrar { font-weight:bold; margin-bottom:16px; }
  .objet { font-weight:bold; margin-bottom:24px; }
  .sign { margin-top:32px; }
  .disc { margin-top:40px; padding-top:8px; border-top:1px solid #ccc; font-size:9pt; color:#888; font-style:italic; }
</style></head><body>
  <div class="exp">${nl2br(expediteur || '[Vos nom et adresse]')}</div>
  <div class="dest">${nl2br(destNom + '\n' + (destAdresse || "[Adresse de l'étude]"))}</div>
  <div class="date">${esc(lieuDate || '[Lieu], le [date]')}</div>
  ${pack.mention_lrar ? `<div class="lrar">${esc(pack.mention_lrar)}</div>` : ''}
  <div class="objet">Objet : ${esc(pack.objet || 'Dénonciation de contestation')}</div>
  <p style="margin:0 0 16px">Madame, Monsieur,</p>
  ${paras}
  <p style="margin:0 0 32px;text-align:justify">${esc(formule)}</p>
  <div class="sign">Signature :</div>
  ${pack.disclaimer ? `<div class="disc">${esc(pack.disclaimer)}</div>` : ''}
</body></html>`;
  };

  const handlePrint = () => {
    const iframe = document.createElement('iframe');
    iframe.setAttribute('aria-hidden', 'true');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    document.body.appendChild(iframe);

    const cw = iframe.contentWindow;
    const doc = cw.document;
    doc.open();
    doc.write(buildLetterHtml());
    doc.close();

    cw.onafterprint = () => {
      setTimeout(() => {
        try {
          document.body.removeChild(iframe);
        } catch (e) {
          /* déjà retirée */
        }
      }, 500);
    };

    setTimeout(() => {
      cw.focus();
      cw.print();
    }, 200);
  };

  return (
    <div className="card p-6 sm:p-8 mb-8">
      {/* En-tête */}
      <div className="mb-6 text-left sm:text-center">
        <div
          className="flex items-start sm:items-center sm:justify-center gap-2 mb-2"
          style={{ color: '#1e3a8a' }}
        >
          <Scale size={22} className="shrink-0 mt-1 sm:mt-0" />
          <h3 className="text-xl sm:text-2xl font-bold leading-tight">
            Contester la saisie devant le juge de l'exécution
          </h3>
        </div>
        <p className="text-gray-600 text-sm sm:text-base">
          Voici votre échéancier et le courrier obligatoire à envoyer au commissaire de justice.
        </p>
      </div>

      {/* Bandeau d'échéance */}
      <div
        className="rounded-lg p-5 mb-6 border"
        style={{ backgroundColor: couleurs.bg, borderColor: couleurs.border }}
      >
        {ech.needs_review ? (
          <div className="flex items-start gap-3">
            <AlertTriangle size={20} className="mt-0.5 shrink-0" style={{ color: couleurs.text }} />
            <div>
              <p className="font-semibold" style={{ color: couleurs.text }}>
                Votre date limite n'a pas pu être calculée automatiquement
              </p>
              <p className="text-sm mt-1" style={{ color: couleurs.text }}>
                {ech.motif_review}
              </p>
            </div>
          </div>
        ) : (
          <div className="flex items-start gap-3">
            <Clock size={20} className="mt-0.5 shrink-0" style={{ color: couleurs.text }} />
            <div>
              <p className="font-semibold" style={{ color: couleurs.text }}>
                {ech.delai_expire
                  ? `Le délai de contestation a expiré le ${formatDateFr(ech.date_limite)}`
                  : `Vous avez jusqu'au ${formatDateFr(ech.date_limite)} pour agir`}
              </p>
              <p className="text-sm mt-1" style={{ color: couleurs.text }}>
                {ech.delai_expire
                  ? "Un avocat reste le mieux placé pour vous dire s'il existe encore une voie. N'attendez pas davantage."
                  : `Soit ${jours} jour${jours > 1 ? 's' : ''}. Ce délai court à compter de ${ech.point_depart_libelle}.`}
              </p>
              <p className="text-xs mt-2 opacity-80" style={{ color: couleurs.text }}>
                {ech.libelle} · {ech.source}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Mise en garde structurante : ce courrier ne saisit pas le juge.
          C'est l'information qui évite à l'utilisateur de croire qu'il a agi alors que son
          délai continue de courir — elle doit donc peser visuellement autant que l'échéance,
          pas ressembler à une note de bas de page. */}
      <div className="rounded-lg p-4 mb-6 border border-amber-300 bg-amber-50">
        <div className="flex items-start gap-3">
          <AlertTriangle size={20} className="mt-0.5 shrink-0 text-amber-700" />
          <div>
            <p className="text-sm text-amber-900">
              <strong>Attention : ce courrier ne saisit pas le juge.</strong> La contestation se
              forme par une assignation, délivrée par un commissaire de justice. La lettre
              ci-dessous est une formalité obligatoire qui l'accompagne : sans elle, la
              contestation est irrecevable.
            </p>
            {ech.sanction && <p className="text-xs text-amber-700 mt-2">{ech.sanction}</p>}
          </div>
        </div>
      </div>

      {/* Mode d'emploi */}
      {etapes.length > 0 && (
        <div className="mb-6">
          <h4 className="font-semibold text-gray-800 mb-3">Les étapes, dans l'ordre</h4>
          <ol className="space-y-3">
            {etapes.map((etape) => (
              <li key={etape.ordre} className="flex items-start gap-3">
                <span
                  className="shrink-0 w-6 h-6 rounded-full text-white text-xs font-bold flex items-center justify-center"
                  style={{ backgroundColor: '#1e3a8a' }}
                >
                  {etape.ordre}
                </span>
                <div>
                  <p className="text-sm text-gray-900">{etape.libelle}</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {etape.echeance
                      ? `À faire avant le ${formatDateFr(etape.echeance)}`
                      : etape.regle
                      ? `Délai : ${etape.regle}`
                      : 'Délai à vérifier sur l\'acte'}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* Représentation par avocat */}
      {pack.avocat_obligatoire && (
        <div className="rounded-lg p-5 mb-6 border border-amber-200 bg-amber-50">
          <div className="flex items-start gap-3">
            <Scale size={20} className="mt-0.5 shrink-0 text-amber-700" />
            <div>
              <p className="font-semibold text-amber-900">Vous devez être représenté par un avocat</p>
              <p className="text-sm text-amber-800 mt-1">{pack.motif_avocat}</p>
              <p className="text-sm text-amber-800 mt-2">
                Envoyez tout de même la lettre ci-dessous : la dénonciation au commissaire de
                justice vous incombe, même représenté. C'est l'assignation qui devra être portée
                par un avocat.
              </p>
            </div>
          </div>
        </div>
      )}
      {!pack.avocat_obligatoire && pack.motif_avocat && (
        <div className="rounded-lg p-4 mb-6 border border-green-200 bg-green-50 flex items-start gap-3">
          <CheckCircle2 size={18} className="mt-0.5 shrink-0 text-green-700" />
          <p className="text-sm text-green-900">{pack.motif_avocat}</p>
        </div>
      )}

      {/* Champs à compléter */}
      <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
        <FileText size={18} />
        Votre lettre de dénonciation
      </h4>
      <div className="grid sm:grid-cols-2 gap-4 mb-6 p-4 bg-blue-50 rounded-lg">
        <label className="block">
          <span className="text-sm font-medium text-gray-700">Vos coordonnées (expéditeur)</span>
          <textarea
            className="mt-1 w-full rounded-md border border-gray-300 p-2 text-sm"
            rows={3}
            value={expediteur}
            onChange={(e) => setExpediteur(e.target.value)}
            placeholder="Nom, prénom, adresse postale"
          />
        </label>
        <label className="block">
          <span className="text-sm font-medium text-gray-700">Adresse de l'étude</span>
          <textarea
            className="mt-1 w-full rounded-md border border-gray-300 p-2 text-sm"
            rows={3}
            value={destAdresse}
            onChange={(e) => setDestAdresse(e.target.value)}
            placeholder="Adresse du commissaire de justice qui a pratiqué la saisie"
          />
        </label>
        <label className="block sm:col-span-2">
          <span className="text-sm font-medium text-gray-700">Lieu et date</span>
          <input
            className="mt-1 w-full rounded-md border border-gray-300 p-2 text-sm"
            value={lieuDate}
            onChange={(e) => setLieuDate(e.target.value)}
            placeholder="Ville, le JJ/MM/AAAA"
          />
        </label>
      </div>

      {/* Aperçu */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-10 text-gray-900 leading-relaxed text-sm sm:text-base">
        <div className="whitespace-pre-line mb-8">{expediteur || '[Vos nom et adresse]'}</div>

        <div className="sm:text-right whitespace-pre-line mb-8">
          {destNom}
          {'\n'}
          {destAdresse || "[Adresse de l'étude]"}
        </div>

        <div className="sm:text-right mb-8">{lieuDate || '[Lieu], le [date]'}</div>

        {pack.mention_lrar && <div className="font-semibold mb-4">{pack.mention_lrar}</div>}

        <div className="font-semibold mb-6">Objet : {pack.objet || 'Dénonciation de contestation'}</div>

        <div className="mb-4">Madame, Monsieur,</div>

        {corps.map((p, i) => (
          <p key={i} className="mb-4 sm:text-justify">
            {p}
          </p>
        ))}

        <p className="mb-8 sm:text-justify">
          {pack.formule_politesse ||
            "Je vous prie d'agréer, Madame, Monsieur, l'expression de mes salutations distinguées."}
        </p>

        <div className="mb-2">Signature :</div>
        <div className="h-16 border-b border-gray-200 w-1/2" />

        {pack.disclaimer && (
          <p className="mt-8 pt-4 border-t border-gray-100 text-xs text-gray-400 italic">{pack.disclaimer}</p>
        )}
      </div>

      <div className="mt-6 text-center">
        <Button
          size="lg"
          onClick={handlePrint}
          className="px-8 py-6 text-lg font-semibold"
          style={{ backgroundColor: '#1e3a8a', color: 'white' }}
        >
          <Printer className="mr-2" size={20} />
          Imprimer / Télécharger la lettre
        </Button>
        <p className="text-xs text-gray-500 mt-3">
          À envoyer en recommandé avec accusé de réception. Conservez la preuve de dépôt.
        </p>
      </div>
    </div>
  );
};

export default SaisineJexPack;
